/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

/**
 * Provides the OIDC scopes which are requested of every identity provider,
 * irrespective of the scopes its configuration carries. `openid` and `email`
 * identify the user, `offline_access` requests the refresh token the silent
 * renewal prefers over a hidden iframe, and `profile` carries the
 * `preferred_username` claim Microsoft Entra asserts in place of `email` for
 * tenant accounts which hold no mailbox.
 */
export const REQUIRED_SCOPES = ['openid', 'email', 'offline_access', 'profile'];

/**
 * Normalizes a configured scope into its individual scopes, accepting both
 * the space-delimited string form and an array of either form. The platform's
 * configuration reaches the framework untyped, so a value which is not a
 * string is discarded rather than coerced.
 */
const toScopeList = (scope?: unknown): string[] => {
  const configured: unknown[] = Array.isArray(scope) ? scope : [scope];
  return configured
    .filter((value): value is string => typeof value === 'string')
    .flatMap((value) => value.split(/\s+/))
    .filter((value) => value !== '');
};

/**
 * Reduces the given scopes to the first occurrence of each, preserving the
 * order in which they were requested.
 */
const unique = (scopes: string[]): string[] => (
  scopes.filter((scope, index) => scopes.indexOf(scope) === index)
);

/**
 * Resolves the scope which is requested of the identity provider, merging the
 * scopes configured for the provider onto those the framework requires. The
 * scopes are merged rather than replaced, as a configuration which persists
 * only `openid` would otherwise drop the scopes the login flow depends on.
 *
 * ```typescript
 *   import {resolveScope} from '@ion/applications/src/authentication/OidcScopes';
 *   ...
 *
 *   const scope = resolveScope('openid api://calamp/access_as_user');
 *   // 'openid email offline_access profile api://calamp/access_as_user' ...
 *
 * ```
 *
 * @param   configuredScope The scope configured for the provider, as either a
 *          space-delimited string or an array of scopes.
 * @returns The space-delimited scope requested of the identity provider.
 */
export const resolveScope = (configuredScope?: unknown): string => (
  unique([...REQUIRED_SCOPES, ...toScopeList(configuredScope)]).join(' ')
);
